import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";
import { useTenant } from "@/lib/tenant";
import { latar } from "@/lib/progress";

/** Peta status → label Indonesia + warna chip. Satu sumber untuk konsistensi tampilan. */
const STATUS: Record<string, { label: string; tone: string }> = {
  PENDING: { label: "Menunggu", tone: "warn" },
  ASSIGNED: { label: "Ditugaskan", tone: "info" },
  IN_PROGRESS: { label: "Dikerjakan", tone: "info" },
  SUBMITTED: { label: "Menunggu Konfirmasi", tone: "warn" },
  COMPLETED: { label: "Selesai", tone: "success" },
  REJECTED: { label: "Ditolak", tone: "danger" },
  CANCELLED: { label: "Dibatalkan", tone: "muted" },
};
const PRIO: Record<string, { label: string; color: string }> = {
  low: { label: "Rendah", color: "var(--color-text-muted)" },
  normal: { label: "Normal", color: "var(--color-text)" },
  high: { label: "Tinggi", color: "var(--status-warn)" },
  urgent: { label: "Mendesak", color: "var(--status-danger)" },
};

const wib = (d?: string | null) => (d ? new Date(new Date(d).getTime() + 7 * 3600e3).toISOString().slice(0, 16).replace("T", " ") : "-");

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  const bg: Record<string, string> = {
    success: "var(--status-success-bg)", danger: "var(--status-danger-bg)",
    warn: "var(--status-warn-bg)", info: "var(--status-info-bg, var(--color-surface-sunken))", muted: "var(--color-surface-sunken)",
  };
  const fg: Record<string, string> = {
    success: "var(--status-success)", danger: "var(--status-danger)",
    warn: "var(--status-warn)", info: "var(--status-info, var(--color-text))", muted: "var(--color-text-muted)",
  };
  return <span className="avio-chip" data-shape="pill" style={{ background: bg[tone] ?? bg.muted, color: fg[tone] ?? fg.muted }}>{children}</span>;
}

interface Row {
  id: string; title: string; location: string | null; targetDate: string | null;
  priority: string; status: string; requesterName: string; assigneeName: string | null;
  createdAt: string; _count: { updates: number; attachments: number };
}

export default function LiputanPage() {
  const { can } = useTenant();
  const canManage = can("manage_liputan");
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(false);
  const [msg, setMsg] = useState("");

  // Form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [priority, setPriority] = useState("normal");
  const [assigneeId, setAssigneeId] = useState("");
  const [assignees, setAssignees] = useState<{ id: string; name: string; hasPhone: boolean }[]>([]);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const qs = filter ? `?status=${filter}` : "";
    const r = await latar(() => fetch(`/api/v1/liputan${qs}`));
    if (r.status === 401) { window.location.href = "/login"; return; }
    if (r.ok) setRows(await r.json());
    setLoading(false);
  }
  useEffect(() => { refresh(); }, [filter]);

  // Koordinator: muat kandidat petugas untuk penugasan langsung saat pengajuan.
  useEffect(() => {
    if (!canManage) return;
    fetch("/api/v1/liputan/assignees").then((r) => (r.ok ? r.json() : [])).then(setAssignees).catch(() => {});
  }, [canManage]);

  async function submit() {
    if (!title.trim()) { setMsg("Judul wajib diisi."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await fetch("/api/v1/liputan", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, description, location, targetDate: targetDate || null, priority, assigneeId: assigneeId || undefined }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "gagal");
      setTitle(""); setDescription(""); setLocation(""); setTargetDate(""); setPriority("normal"); setAssigneeId("");
      setForm(false); setMsg("Permintaan peliputan berhasil dibuat.");
      refresh();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "gagal");
    } finally { setBusy(false); }
  }

  const stat = {
    aktif: rows.filter((r) => ["PENDING", "ASSIGNED", "IN_PROGRESS", "SUBMITTED"].includes(r.status)).length,
    selesai: rows.filter((r) => r.status === "COMPLETED").length,
    total: rows.length,
  };

  const FILTERS = [["", "Semua"], ["PENDING", "Menunggu"], ["ASSIGNED", "Ditugaskan"], ["IN_PROGRESS", "Dikerjakan"], ["SUBMITTED", "Konfirmasi"], ["COMPLETED", "Selesai"]] as const;

  return (
    <div className="avio-rise">
      <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-6)", flexWrap: "wrap" }}>
        <div>
          <h1 className="avio-page-title" style={{ margin: 0 }}>Request Peliputan</h1>
          <div className="avio-t-sm avio-muted">Ajukan & kelola permintaan peliputan lapangan tim humas.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="avio-btn" data-variant="primary" onClick={() => setForm((v) => !v)}>
            <i className={`fa-solid ${form ? "fa-xmark" : "fa-plus"}`} /> {form ? "Tutup" : "Ajukan Peliputan"}
          </button>
        </div>
      </div>

      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-clipboard-list" label="Aktif" value={stat.aktif} sub="sedang berjalan" />
        <Tile c={3} icon="fa-solid fa-circle-check" label="Selesai" value={stat.selesai} sub="terkonfirmasi" />
        <Tile c={5} icon="fa-solid fa-layer-group" label="Total" value={stat.total} sub="semua permintaan" />
      </div>

      {msg && <div className="avio-banner" data-tone="info"><i className="fa-solid fa-circle-info avio-banner__icon" /><div>{msg}</div></div>}

      {form && (
        <Panel icon="fa-solid fa-pen-to-square" title="Permintaan Peliputan Baru">
          <div style={{ display: "grid", gap: "var(--space-5)" }}>
            <label className="avio-field">
              <span className="avio-field__label">Judul / Topik <span style={{ color: "var(--status-danger)" }}>*</span></span>
              <input className="avio-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="mis. Peliputan kunjungan Menteri ke pabrik X" />
            </label>
            <label className="avio-field">
              <span className="avio-field__label">Deskripsi / Konteks</span>
              <textarea className="avio-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Detail acara, narahubung, hal yang perlu diliput…" />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--space-5)" }}>
              <label className="avio-field">
                <span className="avio-field__label">Lokasi</span>
                <input className="avio-input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={300} placeholder="Kota / venue" />
              </label>
              <label className="avio-field">
                <span className="avio-field__label">Target Tanggal</span>
                <input className="avio-input" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
              </label>
              <label className="avio-field">
                <span className="avio-field__label">Prioritas</span>
                <select className="avio-input" value={priority} onChange={(e) => setPriority(e.target.value)}>
                  <option value="low">Rendah</option>
                  <option value="normal">Normal</option>
                  <option value="high">Tinggi</option>
                  <option value="urgent">Mendesak</option>
                </select>
              </label>
              {canManage && (
                <label className="avio-field">
                  <span className="avio-field__label">Petugas Peliput</span>
                  <select className="avio-input" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                    <option value="">— tugaskan nanti —</option>
                    {assignees.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}{a.hasPhone ? "" : " (tanpa WA)"}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div style={{ display: "flex", gap: "var(--space-4)" }}>
              <button className="avio-btn" data-variant="primary" disabled={busy} onClick={submit}>
                <i className={`fa-solid ${busy ? "fa-circle-notch fa-spin" : "fa-paper-plane"}`} /> Kirim Permintaan
              </button>
              <button className="avio-btn" data-variant="secondary" onClick={() => setForm(false)}>Batal</button>
            </div>
          </div>
        </Panel>
      )}

      <Panel icon="fa-solid fa-list-check" title="Daftar Permintaan" flush
        action={
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {FILTERS.map(([v, l]) => (
              <button key={v} className="avio-btn" data-variant={filter === v ? "primary" : "ghost"} data-size="sm" onClick={() => setFilter(v)}>{l}</button>
            ))}
          </div>
        }>
        <div className="avio-tablewrap" style={{ border: 0, borderRadius: 0 }}>
          {loading ? (
            <div style={{ padding: 24 }}><div className="avio-skeleton" style={{ height: 12, width: 240 }} /></div>
          ) : rows.length === 0 ? (
            <div className="avio-empty" style={{ padding: 32 }}>
              <i className="fa-solid fa-video avio-empty__icon" />
              <div className="avio-empty__title">Belum ada permintaan peliputan</div>
              <div className="avio-empty__hint">Klik “Ajukan Peliputan” untuk membuat permintaan pertama.</div>
            </div>
          ) : (
            <table className="avio-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Topik", "Prioritas", "Status", canManage ? "Petugas" : "Pengaju", "Target", "Lampiran", ""].map((h, i) => (
                    <th key={i} style={{ textAlign: "left", padding: "10px 14px", borderBottom: "1px solid var(--color-border)", fontSize: "var(--text-sm)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = STATUS[r.status] ?? { label: r.status, tone: "muted" };
                  const pr = PRIO[r.priority] ?? PRIO.normal;
                  return (
                    <tr key={r.id} style={{ borderBottom: "1px solid var(--color-border-subtle, var(--color-border))" }}>
                      <td style={{ padding: "10px 14px" }}>
                        <Link to={`/a/liputan/${r.id}`} style={{ fontWeight: 600, color: "var(--color-text)" }}>{r.title}</Link>
                        {r.location && <div className="avio-sub"><i className="fa-solid fa-location-dot" /> {r.location}</div>}
                      </td>
                      <td style={{ padding: "10px 14px", color: pr.color, fontWeight: 600, fontSize: "var(--text-sm)" }}>{pr.label}</td>
                      <td style={{ padding: "10px 14px" }}><Chip tone={st.tone}>{st.label}</Chip></td>
                      <td style={{ padding: "10px 14px", fontSize: "var(--text-sm)" }}>{canManage ? (r.assigneeName ?? "—") : r.requesterName}</td>
                      <td style={{ padding: "10px 14px", fontSize: "var(--text-sm)", whiteSpace: "nowrap" }}>{r.targetDate ? wib(r.targetDate).slice(0, 10) : "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: "var(--text-sm)" }}>{r._count.attachments > 0 ? <span><i className="fa-solid fa-paperclip" /> {r._count.attachments}</span> : "—"}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <Link to={`/a/liputan/${r.id}`} className="avio-btn" data-variant="ghost" data-size="sm">Buka <i className="fa-solid fa-chevron-right" /></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Panel>
    </div>
  );
}
